import { setDate, subMonths } from "date-fns";
import type { Meta, NjordPolicy, PoliciesState, Policy, PolicyView } from "./types";
import actions from "~/shared/actions";
import type { PoliseOversiktDto } from "~/shared/api/usePoliseOversikt";
import type { Action } from "~/utils/actions";

const defaultMeta: Meta = {
	isUpdatingBeneficiaries: false,
	hasUpdatedBeneficiaries: false,
	hasFetchedFictiveUtbetalingMarketValue: false,
};

function receivePolicies(state: PoliciesState<PolicyView>, action: Action<PoliseOversiktDto>) {
	const { poliser, poliserAnnenDistributor, hideEpkInngangForRadgiver, totalJobbSparing, totalPrivatSparing, totalAnnenSparing } = action.data;
	const { list } = state;

	poliser.forEach((policy) => {
		const existing = list.find((candidate) => candidate.polisenummer === policy.polisenummer && candidate.produktId === policy.produktId);
		if (existing) {
			// The list only carries utbetalingDato/utbetalingKontonummer; keep the rest of utbetaling from loaded details
			Object.assign(existing, policy, {
				utbetaling: {
					...existing.utbetaling,
					utbetalingDato: policy.utbetaling?.utbetalingDato,
					utbetalingKontonummer: policy.utbetaling?.utbetalingKontonummer,
					utbetalingStatus: policy.utbetaling?.utbetalingStatus,
				},
			});
		} else {
			list.push({
				...policy,
				meta: defaultMeta,
				holdings: {},
			} as PolicyView);
		}
	});
	return { ...state, list: [...list], poliserAnnenDistributor, hideEpkInngangForRadgiver, totalJobbSparing, totalPrivatSparing, totalAnnenSparing };
}

function receivePolicy(state, action) {
	const { policy } = action.data;

	if (!state.list.length) {
		return { ...state, list: [policy] };
	}
	const list = state.list.map((storedPolicy: Policy) => {
		if (storedPolicy.polisenummer !== policy.polisenummer) {
			return storedPolicy;
		}
		const holdings = !Object.keys(storedPolicy.holdings).length ? policy.holdings || {} : storedPolicy.holdings;

		return {
			...storedPolicy,
			...policy,
			errorKode: policy.errorKode || storedPolicy.holdings?.errorCode,
			holdings,
			// For products with garanti under utbetaling, the investment profile
			// is switched before the product id changes, so we can risk having
			// a product with a none-garanti product id which is still garanti.
			// In that case, override garanti flag based on investmentProfile.
			garanti: policy.garanti || storedPolicy.holdings?.investmentProfile === "Garanti",
		};
	});
	return { ...state, list };
}

function receiveNjordPolicy(state: PoliciesState<NjordPolicy>, action: Action<NjordPolicy>) {
	const policy = action.data;
	return {
		...state,
		list: state.list
			.filter((p) => p.polisenummer !== policy.polisenummer)
			.concat({
				...policy,
				holdings: {},
				isComplete: true,
			} as NjordPolicy),
	};
}

function receiveSalaryHistory(state, action) {
	const { salaryHistory, polisenummer } = action.data;

	const list = state.list.map((policy: Policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		return { ...policy, salaryHistory };
	});
	return { ...state, list };
}

function updatedSavingAmount(state, action) {
	const { manualStepsRequired, polisenummer, savingAmount } = action.data;

	const list = state.list.map((policy: Policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		return {
			...policy,
			errorCode: undefined,
			spareavtale: {
				...policy.spareavtale,
				sparebelop: savingAmount,
			},
			manualStepsRequired,
		};
	});
	return { ...state, list };
}

function updateSavingAmountError(state, action) {
	const { polisenummer, errorCode } = action.data;

	const list = state.list.map((policy: Policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		return {
			...policy,
			errorCode,
			manualStepsRequired: undefined,
		};
	});
	return { ...state, list };
}

function updatedKapitalforvalter(state, action) {
	const { polisenummer, kapitalforvalter } = action.data;

	const list = state.list.map((policy: Policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		return {
			...policy,
			holdings: {
				...policy.holdings,
				kapitalforvalter,
			},
		};
	});
	return { ...state, list };
}

function updatedNedtrapping(state, action) {
	const { polisenummer, nedtrapping } = action.data;

	const list = state.list.map((policy: Policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		return {
			...policy,
			holdings: {
				...policy.holdings,
				nedtrapping,
			},
		};
	});
	return { ...state, list };
}

function updatedPaymentDeal(state, action) {
	const { polisenummer, paymentDeal, success } = action.data;

	const list = state.list.map((policy: Policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		return {
			...policy,
			spareavtale: {
				...policy.spareavtale,
				sparebelop: success ? Number(paymentDeal.savingAmount) : policy.spareavtale?.sparebelop,
				sparetrekkdato: success ? paymentDeal.savingDayOfMonth : policy.spareavtale?.sparetrekkdato,
			},
			paymentDealDeleted: false, // to avoid showing two receipt messages at the same time
			paymentDealUpdated: success,
			paymentDealUpdateFailed: !success,
		};
	});
	return { ...state, list };
}

function deletedPaymentDeal(state, action) {
	const { polisenummer } = action.data;

	const list = state.list.map((policy: Policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		return {
			...policy,
			spareavtale: {
				...policy.spareavtale,
				avtaleKontonummer: null,
				sparebelop: 0,
				sparetrekkdato: null,
				spareFrekvens: null,
			},
			paymentDealDeleted: true,
			paymentDealUpdated: false, // to avoid showing two receipt messages at the same time
		};
	});
	return { ...state, list };
}

function creatingPaymentDeal(state, action) {
	const { polisenummer } = action.data;

	const list = state.list.map((policy: Policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		return {
			...policy,
			paymentDealDeleted: null,
			paymentDealUpdated: null,
		};
	});
	return { ...state, list };
}

function createdPaymentDeal(state, action) {
	const { polisenummer, paymentDeal, success } = action.data;

	const list = state.list.map((policy: Policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		if (success) {
			return {
				...policy,
				paymentDealUpdateFailed: false,
				spareavtale: {
					...policy.spareavtale,
					avtaleKontonummer: paymentDeal.accountNumber,
					sparebelop: Number(paymentDeal.savingAmount),
					sparetrekkdato: paymentDeal.savingDayOfMonth,
					spareFrekvens: "per måned",
				},
			};
		}
		return { ...policy, paymentDealUpdateFailed: true };
	});
	return { ...state, list };
}

function updatedUtbetalingStart(state, action) {
	const { polisenummer, data } = action.data;

	const list = state.list.map((policy: Policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		const utbetalingEnd = data.utbetalingEnd.getTime();
		const utbetalingStart = data.utbetalingStart.getTime();

		const firstUtbetaling = setDate(data.utbetalingStart, 20).getTime();
		const lastUtbetaling = subMonths(setDate(data.utbetalingEnd, 20), 1).getTime();

		return {
			...policy,
			disbursementDatesChanged: true,
			utbetaling: {
				...policy.utbetaling,
				utbetalingSlutt: utbetalingEnd,
				utbetalingStart,
				forsteUtbetaling: firstUtbetaling,
				sisteUtbetaling: lastUtbetaling,
			},
		};
	});
	return { ...state, list };
}

function receiveHoldings(state, action) {
	const { polisenummer, holdings } = action.data;

	const list = state.list.map((policy: Policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		return {
			...policy,
			holdings,
		};
	});
	return { ...state, list };
}

function receiveFictiveUtbetalingMarketValue(state, action) {
	const { polisenummer, fictiveUtbetalingMarketValue } = action.data;

	const list = state.list.map((policy: Policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		return {
			...policy,
			fictiveUtbetalingMarketValue,
			meta: {
				...policy.meta,
				hasFetchedFictiveUtbetalingMarketValue: true,
			},
		};
	});
	return { ...state, list };
}

function updatedForvaltning(state, action) {
	const { forvaltning, polisenummer } = action.data;

	const list = state.list.map((policy: Policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		return {
			...policy,
			forvaltning,
		};
	});
	return { ...state, list };
}

function updatedSpareprofil(state, action) {
	const {
		polisenummer,
		newInvestmentProgram: { spareprofil, switchToGaranti },
	} = action.data;

	const list = state.list.map((policy: Policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		return {
			...policy,
			garanti: switchToGaranti ? true : policy.garanti,
			spareprofil,
		};
	});
	return { ...state, list };
}

function updateIpsDeposit(state, action) {
	const { polisenummer, deposit } = action.data;

	const list = state.list.map((policy: Policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		return {
			...policy,
			ipsDeposit: deposit,
		};
	});
	return { ...state, list };
}

function resetPolicyViews(state) {
	return {
		...state,
		list: [],
	};
}

function updateBeneficiariesRequest(state: PoliciesState, action: Action) {
	const { polisenummer } = action.data;

	const list = state.list.map((policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		return {
			...policy,
			meta: {
				...policy.meta,
				isUpdatingBeneficiaries: true,
			},
		};
	});
	return { ...state, list };
}

function updateBeneficiariesResponse(state: PoliciesState, action: Action) {
	const { polisenummer } = action.data;

	const list = state.list.map((policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		return {
			...policy,
			meta: {
				...policy.meta,
				isUpdatingBeneficiaries: false,
				hasUpdatedBeneficiaries: true,
			},
		};
	});
	return { ...state, list };
}

function updateSavedRecommendation(state: PoliciesState, action: Action) {
	const { polisenummer, result } = action.data;
	const list = state.list.map((policy) => {
		if (policy.polisenummer !== polisenummer) {
			return policy;
		}
		return {
			...policy,
			lagretAnbefaling: result,
		};
	});
	return { ...state, list };
}

export const defaultState = {
	list: [],
	totalPrivatSparing: 0,
	totalJobbSparing: 0,
	totalAnnenSparing: 0,
};

export default function policiesReducer(state = defaultState, action: Action) {
	switch (action.type) {
		case actions.POLICIES_RESPONSE:
			return receivePolicies(state, action as Action<PoliseOversiktDto>);
		case actions.HOLDINGS_RESPONSE:
			return receiveHoldings(state, action);
		case actions.FICTIVE_UTBETALING_MARKET_VALUE_RESPONSE:
			return receiveFictiveUtbetalingMarketValue(state, action);
		case actions.AGGREGATED_POLICY_RESPONSE:
		case actions.POLICY_DETAILS_RESPONSE:
			return receivePolicy(state, action);
		case actions.NJORD_POLICY_DETAILS_RESPONSE:
			return receiveNjordPolicy(state, action as Action<NjordPolicy>);
		case actions.SALARY_HISTORY_RESPONSE:
			return receiveSalaryHistory(state, action);
		case actions.UPDATE_SAVING_AMOUNT_RESPONSE:
			return updatedSavingAmount(state, action);
		case actions.UPDATE_SAVING_AMOUNT_FAILURE:
			return updateSavingAmountError(state, action);
		case actions.UPDATE_KAPITALFORVALTER_RESPONSE:
			return updatedKapitalforvalter(state, action);
		case actions.UPDATE_NEDTRAPPING_RESPONSE:
			return updatedNedtrapping(state, action);
		case actions.UPDATE_PAYMENT_DEAL_RESPONSE:
			return updatedPaymentDeal(state, action);
		case actions.DELETE_PAYMENT_DEAL_RESPONSE:
			return deletedPaymentDeal(state, action);
		case actions.CREATE_PAYMENT_DEAL_REQUEST:
			return creatingPaymentDeal(state, action);
		case actions.CREATE_PAYMENT_DEAL_RESPONSE:
			return createdPaymentDeal(state, action);
		case actions.UPDATE_UTBETALING_START_RESPONSE:
			return updatedUtbetalingStart(state, action);
		case actions.UPDATE_FORVALTNING_RESPONSE:
			return updatedForvaltning(state, action);
		case actions.INVESTMENT_PROFILE_UPDATE_RESPONSE:
			return updatedSpareprofil(state, action);
		case actions.LOAD_IPS_DEPOSIT_RESPONSE:
			return updateIpsDeposit(state, action);
		case actions.RESET_POLICIES:
			return resetPolicyViews(state);
		case actions.UPDATE_BENEFICIARIES_REQUEST:
			return updateBeneficiariesRequest(state, action);
		case actions.UPDATE_BENEFICIARIES_RESPONSE:
			return updateBeneficiariesResponse(state, action);
		case actions.SAVE_RECOMMENDATION_RESPONSE:
			return updateSavedRecommendation(state, action);
		default:
			return state;
	}
}
